import { assertIsDefined, isDefined, isNotDefined } from '@sgme/fp';

import type { CashEspPrice } from '@/store/cash/selectors/prices.types.ts';
import type { SpotMarginWithTiering } from '@/store/cash/state.ts';
import type {
  EspExternalUserLadder,
  EspInternalUserLadder,
  EspStreamClientPriced,
  EspStreamPriced,
  EspStreamTraderPriced,
} from '@/store/esp/state.ts';
import { getLadderForNotional } from '@/store/esp/utils.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';
import { roundToFixed } from '@/utils/number.ts';

// ██████╗ ██████╗ ██╗ ██████╗███████╗███████╗
// ██╔══██╗██╔══██╗██║██╔════╝██╔════╝██╔════╝
// ██████╔╝██████╔╝██║██║     █████╗  ███████╗
// ██╔═══╝ ██╔══██╗██║██║     ██╔══╝  ╚════██║
// ██║     ██║  ██║██║╚██████╗███████╗███████║
// ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝╚══════╝

/**
 * Original code found in:
 *
 * getCashEspSpotPriceForExecution()
 * https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/292acd72197b41c015dc53c0053a43ec087ca234/fx-web/app/src/state/fxCashs/selectors/fxCashsRfsStreamsSelectors.ts
 * -> get the margin with getCashSpotMarginValue()
 * -> get the price with getCashEspSpotPriceWithMarginForExecution()
 *
 * getCashSpotMarginValue()
 * https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/292acd72197b41c015dc53c0053a43ec087ca234/fx-web/app/src/state/fxCashs/selectors/fxCashsRfsStreamsSelectors.ts
 * -> get the margin from the tile
 *
 * getCashEspSpotPriceWithMarginForExecution()
 * https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/51ae89b7d6dc08d54c912cab22f3d3745db69136/fx-web/app/src/state/fxCashs/selectors/fxCashsSelectors.ts
 * -> get the price/ladder to use with getEspPriceByNotional()
 * -> and apply the commercial margin to the price
 *
 * getEspPriceByNotional()
 * https://sgithub.fr.world.socgen/SGMARKETS-EXECUTION/fx/blob/8e78b6c8b2f53759d29910583e7f3579221683c6/fx-web/app/src/state/esp/utils.ts
 * -> find the compatible ladder from the ESP price with the notional and the notional currency (to get the notional in the first currency,
 * because the threshold is in the first currency)
 */

export type EspPrices = InvalidPrices | NoLadderEspPrices | CashEspPrice;

export type InvalidPrices = {
  status: 'invalid-prices';
};

// temporary type in the process shows that a RFS request is required
export type NoLadderEspPrices = {
  status: 'no-ladder';
};

export type EspPriceContext = {
  espPrice: EspStreamPriced;
  notional: number | undefined;
  notionalCurrencyIndex: CurrencyIndex;
  currencyPair: string;
  currencyPairPrecision: number;
  margin: SpotMarginWithTiering;
  isInternalSale: boolean;
  isStreamingUnusable: boolean;
  /** user level clearance AND workspace unlocked */
  canTrade: boolean;
};

export function getEspPrices(context: EspPriceContext): EspPrices {
  const {
    espPrice,
    notional,
    notionalCurrencyIndex,
    currencyPair,
    currencyPairPrecision,
    margin,
    isInternalSale,
    isStreamingUnusable,
    canTrade,
  } = context;

  if (isInternalSale && 'valid' in espPrice && espPrice.valid === false) {
    return {
      status: 'invalid-prices' as const,
    };
  }

  const ladder = getLadderForNotional(espPrice, notional, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return {
      status: 'no-ladder' as const,
    };
  }

  // precision used for the bid/ask computation below comes from the ESP stream itself (espPrice.currencyPairPrecision),
  // not from the `currencyPairPrecision` context param (sourced from currencies.json config) returned at the end of
  // this function. Both are expected to always match for a given currency pair.
  const precision = espPrice.currencyPairPrecision;

  const commercialMarginAmount =
    espPrice.priceType === 'ESP.TRADER.PRICE'
      ? getSpotMarginAmount(notional, notionalCurrencyIndex, margin, espPrice)
      : { bid: 0, ask: 0 };

  // commercialMarginAmountCurrency can't be undefined without a bug: currencyPair is always "XXX/YYY"
  // and commercialMarginAmountCurrencyIndex is always 1 or 2
  const commercialMarginAmountCurrency = currencyPair.split('/')[margin.commercialMarginAmountCurrencyIndex - 1];
  assertIsDefined(
    commercialMarginAmountCurrency,
    `currencyPair ${currencyPair} does not have a currency at index ${margin.commercialMarginAmountCurrencyIndex}`,
  );

  const commercialMarginSpotPoints = margin.commercialMarginSpotPoints ?? { bid: 0, ask: 0 };

  const computedPrices =
    espPrice?.priceType === 'ESP.TRADER.PRICE'
      ? getRatesForInternalUser(espPrice, ladder as EspInternalUserLadder, commercialMarginSpotPoints, precision)
      : getRatesForExternalUser(espPrice, ladder as EspExternalUserLadder, precision);

  const { rates, ratesWithoutMargin, spread } = computedPrices;

  /**
   * ESP:
   * - isStreamingUnusable -> false
   * - notional === null || espStreamState.status !== 'PRICING' -> false
   * - (productName === 'FxSpot' || (ndfEspExecutable && productName === 'FxNdf') && isStreamValidForUser(ladderIndex, espStreamState)
   * - else -> false
   *
   * isStreamValidForUser: selected ladder valid
   */
  const isExecutable =
    !isStreamingUnusable &&
    isDefined(notional) &&
    notional > 0 &&
    espPrice.status === 'PRICED' &&
    (ladder?.valid ?? false);

  const isReadonly = !canTrade || isStreamingUnusable;

  return {
    status: 'esp-price' as const,

    currencyPairPrecision,

    isStreamingUnusable,
    isExecutable,
    isReadonly,
    isSaleTier: margin.isEspDefaultTiering,

    frontTimestamp: espPrice.frontTimestamp,
    backTimestamp: espPrice.backTimestamp,

    commercialMarginSpotPoints,
    commercialMarginDefaultSpotPoints: margin.commercialMarginDefaultSpotPoints ?? { bid: 0, ask: 0 },

    commercialMarginAmount,
    commercialMarginAmountCurrencyIndex: margin.commercialMarginAmountCurrencyIndex,
    commercialMarginAmountCurrency,

    rates,
    ratesWithoutMargin,
    spread,

    espPrice,
  };
}

// ███╗   ███╗ █████╗ ██████╗  ██████╗ ██╗███╗   ██╗     █████╗ ███╗   ███╗ ██████╗ ██╗   ██╗███╗   ██╗████████╗
// ████╗ ████║██╔══██╗██╔══██╗██╔════╝ ██║████╗  ██║    ██╔══██╗████╗ ████║██╔═══██╗██║   ██║████╗  ██║╚══██╔══╝
// ██╔████╔██║███████║██████╔╝██║  ███╗██║██╔██╗ ██║    ███████║██╔████╔██║██║   ██║██║   ██║██╔██╗ ██║   ██║
// ██║╚██╔╝██║██╔══██║██╔══██╗██║   ██║██║██║╚██╗██║    ██╔══██║██║╚██╔╝██║██║   ██║██║   ██║██║╚██╗██║   ██║
// ██║ ╚═╝ ██║██║  ██║██║  ██║╚██████╔╝██║██║ ╚████║    ██║  ██║██║ ╚═╝ ██║╚██████╔╝╚██████╔╝██║ ╚████║   ██║
// ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚═╝╚═╝  ╚═══╝    ╚═╝  ╚═╝╚═╝     ╚═╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═══╝   ╚═╝

export const getSpotMarginAmount = (
  notional: number | undefined,
  notionalCurrencyIndex: CurrencyIndex,
  spotMargin: SpotMarginWithTiering,
  espPrice: EspStreamTraderPriced,
) => {
  if (isNotDefined(notional)) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const marginAmountCurrencyIndex = spotMargin.commercialMarginAmountCurrencyIndex;

  // currency pair EUR/USD
  // sample:
  // - notional currency EUR
  // - margin amount currency USD
  // => "1/2"
  switch (`${notionalCurrencyIndex}/${marginAmountCurrencyIndex}`) {
    case '1/1': // in sample: EUR/EUR
      return getSpotMarginCcy1AmountFromNotionalInCcy1(notional, notionalCurrencyIndex, spotMargin, espPrice);
    case '1/2': // in sample: EUR/USD
      return getSpotMarginCcy1AmountFromNotionalInCcy2(notional, spotMargin, espPrice);
    case '2/1': // in sample: USD/EUR
      return getSpotMarginCcy2AmountFromNotionalInCcy1(notional, notionalCurrencyIndex, spotMargin, espPrice);
    case '2/2': // in sample: USD/USD
      return getSpotMarginCcy2AmountFromNotionalInCcy2(notional, notionalCurrencyIndex, spotMargin, espPrice);
    default:
      throw new Error('unexpected currency selection');
  }
};

const getSpotMarginCcy1AmountFromNotionalInCcy1 = (
  notional: number,
  notionalCurrencyIndex: CurrencyIndex,
  spotMargin: SpotMarginWithTiering,
  espPrice: EspStreamTraderPriced,
) => {
  const bidTotalMargin = convertPointsToRate(
    spotMargin.commercialMarginSpotPoints?.bid ?? 0,
    espPrice.currencyPairPrecision,
  );
  const askTotalMargin = convertPointsToRate(
    spotMargin.commercialMarginSpotPoints?.ask ?? 0,
    espPrice.currencyPairPrecision,
  );

  const bidMarkupCcy2 = notional * bidTotalMargin;
  const askMarkupCcy2 = notional * askTotalMargin;

  const ladder = getLadderForNotional(espPrice, notional, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const allInAsk = convertPointsToRate(espPrice.midRate + ladder.traderAsk, espPrice.currencyPairPrecision);
  const allInBid = convertPointsToRate(espPrice.midRate + ladder.traderBid, espPrice.currencyPairPrecision);

  return {
    bid: Math.round(askMarkupCcy2 / allInBid),
    ask: Math.round(bidMarkupCcy2 / allInAsk),
  };
};

const getSpotMarginCcy1AmountFromNotionalInCcy2 = (
  notional: number,
  spotMargin: SpotMarginWithTiering,
  espPrice: EspStreamTraderPriced,
) => {
  const bidTotalMargin = convertPointsToRate(
    spotMargin.commercialMarginSpotPoints?.bid ?? 0,
    espPrice.currencyPairPrecision,
  );
  const askTotalMargin = convertPointsToRate(
    spotMargin.commercialMarginSpotPoints?.ask ?? 0,
    espPrice.currencyPairPrecision,
  );

  const bidMarkupCcy2 = notional * bidTotalMargin;
  const askMarkupCcy2 = notional * askTotalMargin;

  return {
    bid: Math.round(bidMarkupCcy2),
    ask: Math.round(askMarkupCcy2),
  };
};

const getSpotMarginCcy2AmountFromNotionalInCcy1 = (
  notional: number,
  notionalCurrencyIndex: CurrencyIndex,
  spotMargin: SpotMarginWithTiering,
  espPrice: EspStreamTraderPriced,
) => {
  const bidTotalMargin = spotMargin.commercialMarginSpotPoints?.bid ?? 0;
  const askTotalMargin = spotMargin.commercialMarginSpotPoints?.ask ?? 0;

  const ladder = getLadderForNotional(espPrice, notional, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const allInAsk = convertPointsToRate(espPrice.midRate + ladder.traderAsk, espPrice.currencyPairPrecision);
  const allInBid = convertPointsToRate(espPrice.midRate + ladder.traderBid, espPrice.currencyPairPrecision);

  const traderBid = convertPointsToRate(
    espPrice.midRate + ladder.traderBid + bidTotalMargin,
    espPrice.currencyPairPrecision,
  );
  const traderAsk = convertPointsToRate(
    espPrice.midRate + ladder.traderAsk + askTotalMargin,
    espPrice.currencyPairPrecision,
  );

  const conversionFactorBid = 1 / allInBid - 1 / traderBid;
  const conversionFactorAsk = 1 / allInAsk - 1 / traderAsk;

  const bidMarkupCcy1 = notional * conversionFactorBid;
  const askMarkupCcy1 = notional * conversionFactorAsk;

  return {
    bid: Math.round(askMarkupCcy1),
    ask: Math.round(bidMarkupCcy1),
  };
};

const getSpotMarginCcy2AmountFromNotionalInCcy2 = (
  notional: number,
  notionalCurrencyIndex: CurrencyIndex,
  spotMargin: SpotMarginWithTiering,
  espPrice: EspStreamTraderPriced,
) => {
  const bidTotalMargin = spotMargin.commercialMarginSpotPoints?.bid ?? 0;
  const askTotalMargin = spotMargin.commercialMarginSpotPoints?.ask ?? 0;

  const ladder = getLadderForNotional(espPrice, notional, notionalCurrencyIndex);

  if (isNotDefined(ladder)) {
    return {
      bid: 0,
      ask: 0,
    };
  }

  const bidRateWithoutMargin = convertPointsToRate(espPrice.midRate + ladder.traderBid, espPrice.currencyPairPrecision);
  const askRateWithoutMargin = convertPointsToRate(espPrice.midRate + ladder.traderAsk, espPrice.currencyPairPrecision);

  const bidRateWithMargin = convertPointsToRate(
    espPrice.midRate + ladder.traderBid + bidTotalMargin,
    espPrice.currencyPairPrecision,
  );
  const askRateWithMargin = convertPointsToRate(
    espPrice.midRate + ladder.traderAsk + askTotalMargin,
    espPrice.currencyPairPrecision,
  );

  const bidConversionFactor = 1 / bidRateWithoutMargin - 1 / bidRateWithMargin;
  const askConversionFactor = 1 / askRateWithoutMargin - 1 / askRateWithMargin;

  return {
    bid: Math.round(notional * askConversionFactor * bidRateWithoutMargin),
    ask: Math.round(notional * bidConversionFactor * askRateWithoutMargin),
  };
};

// ██████╗  █████╗ ████████╗███████╗███████╗
// ██╔══██╗██╔══██╗╚══██╔══╝██╔════╝██╔════╝
// ██████╔╝███████║   ██║   █████╗  ███████╗
// ██╔══██╗██╔══██║   ██║   ██╔══╝  ╚════██║
// ██║  ██║██║  ██║   ██║   ███████╗███████║
// ╚═╝  ╚═╝╚═╝  ╚═╝   ╚═╝   ╚══════╝╚══════╝

/**
 * Calculates the rates and spread for an internal user based on the provided ESP price, commercial margin spot points,
 * notional amount, notional currency index, and precision.
 */
const getRatesForInternalUser = (
  espPrice: EspStreamTraderPriced,
  ladder: EspInternalUserLadder,
  commercialMarginSpotPoints: { bid: number; ask: number },
  currencyPairPrecision: number,
) => {
  // traderBid is a negative offset from mid (e.g. -577), traderAsk is positive (e.g. +578)
  // the commercial margin widens the spread: it lowers the bid and raises the ask
  const bidRateInPoints = espPrice.midRate + ladder.traderBid - commercialMarginSpotPoints.bid;
  const askRateInPoints = espPrice.midRate + ladder.traderAsk + commercialMarginSpotPoints.ask;

  return {
    rates: {
      bid: convertPointsToRate(bidRateInPoints, currencyPairPrecision),
      ask: convertPointsToRate(askRateInPoints, currencyPairPrecision),
    },

    ratesWithoutMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.traderBid, currencyPairPrecision),
      ask: convertPointsToRate(espPrice.midRate + ladder.traderAsk, currencyPairPrecision),
    },

    // 1 pip = 10 points (same convention as getRfsPrices), verified by esp.test.ts
    spread: roundToFixed((askRateInPoints - bidRateInPoints) / 10, 1), // not in points, but in pips
  };
};

/**
 * Calculates the rates and spread for an external user based on the provided ESP price, notional amount,
 * notional currency index, and precision.
 */
const getRatesForExternalUser = (
  espPrice: EspStreamClientPriced,
  ladder: EspExternalUserLadder,
  currencyPairPrecision: number,
) => {
  // clientBid is a negative offset from mid (e.g. -611), clientAsk is positive (e.g. +613)
  const bidRateInPoints = espPrice.midRate + ladder.clientBid;
  const askRateInPoints = espPrice.midRate + ladder.clientAsk;

  return {
    rates: {
      bid: convertPointsToRate(bidRateInPoints, currencyPairPrecision),
      ask: convertPointsToRate(askRateInPoints, currencyPairPrecision),
    },

    ratesWithoutMargin: {
      bid: convertPointsToRate(espPrice.midRate + ladder.clientBid, currencyPairPrecision),
      ask: convertPointsToRate(espPrice.midRate + ladder.clientAsk, currencyPairPrecision),
    },

    // 1 pip = 10 points (same convention as getRfsPrices), verified by esp.test.ts
    spread: roundToFixed((askRateInPoints - bidRateInPoints) / 10, 1), // not in points, but in pips
  };
};
